import { Link } from "react-router-dom";
import { CalendarDays, FileText, FileSpreadsheet, MapPin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { API, fmtDate, slug } from "@/lib/api";

export const publicFile = (id) => `${API}/public/files/${id}`;
export const classeFoto = (c) => (c.foto_id ? publicFile(c.foto_id) : `/classi/${slug(c.categoria)}.jpg`);

const TECH = [
  ["lunghezza", "Lunghezza f.t."], ["larghezza", "Larghezza"], ["peso", "Peso"],
  ["randa_fiocco", "Randa e fiocco"], ["spinnaker", "Spinnaker asimm."], ["equipaggio", "Equipaggio"],
];

export const ClasseCard = ({ c }) => (
  <article className="bg-white border border-slate-200 rounded-2xl overflow-hidden group flex flex-col" data-testid={`classe-card-${slug(c.categoria)}`}>
    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
      <img src={classeFoto(c)} alt={c.categoria} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <h3 className="font-heading text-3xl font-bold uppercase text-[#0B192C]">{c.categoria}</h3>
      <p className="text-sm text-slate-600 mt-2 mb-5 whitespace-pre-line">{c.descrizione}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mt-auto border-t pt-4">
        {TECH.map(([k, l]) => (
          <div key={k}>
            <dt className="text-[10px] font-mono-x uppercase tracking-wider text-muted-foreground">{l}</dt>
            <dd className="text-sm font-semibold text-[#0B192C]">{c[k] || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  </article>
);

export const RegataRow = ({ r }) => (
  <li className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 py-5 border-b border-white/10" data-testid={`landing-regata-${r.id}`}>
    <div className="flex items-center gap-2 font-mono-x text-emerald-400 text-sm sm:w-60 shrink-0 whitespace-nowrap">
      <CalendarDays className="h-4 w-4" />{fmtDate(r.data_inizio)}{r.data_fine !== r.data_inizio && ` – ${fmtDate(r.data_fine)}`}
    </div>
    <div className="flex-1">
      <div className="font-heading text-xl uppercase">{r.denominazione || r.club_nome}</div>
      <div className="text-sm text-white/60 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{r.club_numero && `n.${r.club_numero} · `}{r.club_nome} · {r.luogo}</div>
    </div>
    <div className="flex flex-wrap gap-1.5">
      <Badge className="bg-white/10 text-white hover:bg-white/10">{r.tipo}</Badge>
      {r.categorie_barca.map((c) => <Badge key={c} variant="outline" className="border-white/30 text-white/80">{c}</Badge>)}
    </div>
  </li>
);

export const RankingClasse = ({ categoria, items }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`ranking-classe-${slug(categoria)}`}>
    <h3 className="font-heading text-2xl uppercase text-[#0B192C] mb-4">{categoria}</h3>
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground">Nessuna ranking pubblicata.</p>
    ) : (
      <ul className="space-y-3">
        {items.map((r) => {
          const Icon = r.formato === "pdf" ? FileText : FileSpreadsheet;
          return (
            <li key={r.id}>
              <a href={publicFile(r.file_id)} target="_blank" rel="noreferrer" className="flex gap-3 items-start group" data-testid={`ranking-link-${r.id}`}>
                <Icon className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  <span className="font-medium text-[#0B192C] group-hover:underline">{r.titolo}</span>
                  <span className="block text-xs text-muted-foreground">{fmtDate(r.data)} · {r.formato.toUpperCase()}</span>
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    )}
  </div>
);

export const CalendarioLink = ({ logged }) => (
  <Link to={logged ? "/regate" : "/login"} className="inline-block mt-8 text-emerald-400 hover:text-emerald-300 font-heading uppercase tracking-wider" data-testid="landing-calendario-link">
    Calendario completo →
  </Link>
);
